import { sanitizeHtml } from './sanitize-html.functions';

const INDENT_EM = 3;
const FONT_FAMILIES: Record<string, string> = { serif: 'serif', monospace: 'monospace' };

const INDENT_CLASS = /ql-indent-(\d+)/;

const indentOf = (item: HTMLElement): number => Number(INDENT_CLASS.exec(item.className)?.[1] ?? 0);

const needsRebuild = (items: HTMLElement[]): boolean =>
    items.some(item => item.dataset['list'] !== undefined || INDENT_CLASS.test(item.className));

const createPlaceholderItem = (doc: Document): HTMLLIElement => {
    const placeholder = doc.createElement('li');
    placeholder.style.listStyleType = 'none';
    return placeholder;
};

const rebuildList = (list: Element, items: HTMLElement[]): Element[] => {
    const doc = list.ownerDocument;
    const roots: Element[] = [];
    const stack: Element[] = [];
    items.forEach((item) => {
        const level = indentOf(item);
        const bullet = item.dataset['list'] ? item.dataset['list'] === 'bullet' : list.tagName === 'UL';
        const tag = bullet ? 'ul' : 'ol';
        stack.length = Math.min(stack.length, level + 1);
        if (stack.length === level + 1 && stack[level].tagName.toLowerCase() !== tag) {
            stack.pop();
        }
        while (stack.length <= level) {
            const container = doc.createElement(tag);
            const parent = stack.at(-1);
            if (!parent) {
                roots.push(container);
            } else {
                const host = parent.lastElementChild ?? parent.appendChild(createPlaceholderItem(doc));
                host.append(container);
            }
            stack.push(container);
        }
        delete item.dataset['list'];
        item.classList.remove(`ql-indent-${level}`);
        if (!item.classList.length) {
            item.removeAttribute('class');
        }
        stack[level].append(item);
    });
    return roots;
};

const convertLists = (root: HTMLElement): void => {
    root.querySelectorAll('ol, ul').forEach((list) => {
        const items = Array.from(list.children).filter((child): child is HTMLElement => child instanceof HTMLElement && child.tagName === 'LI');
        if (needsRebuild(items)) {
            list.replaceWith(...rebuildList(list, items));
        }
    });
};

const convertCodeBlocks = (root: HTMLElement): void => {
    root.querySelectorAll('.ql-code-block-container').forEach((container) => {
        const pre = container.ownerDocument.createElement('pre');
        pre.textContent = Array.from(container.querySelectorAll('.ql-code-block')).map(line => line.textContent).join('\n');
        container.replaceWith(pre);
    });
};

const convertClasses = (root: HTMLElement): void => {
    root.querySelectorAll<HTMLElement>('[class*="ql-"]').forEach((element) => {
        const isRtl = element.classList.contains('ql-direction-rtl') || element.dir === 'rtl';
        const classes = Array.from(element.classList).filter(name => name.startsWith('ql-'));
        classes.forEach((name) => {
            const align = /^ql-align-(\w+)$/.exec(name)?.[1];
            const indent = /^ql-indent-(\d+)$/.exec(name)?.[1];
            const font = /^ql-font-(\w+)$/.exec(name)?.[1];
            if (align) {
                element.style.textAlign = align;
            } else if (indent) {
                element.style[isRtl ? 'paddingRight' : 'paddingLeft'] = `${Number(indent) * INDENT_EM}em`;
            } else if (font && FONT_FAMILIES[font]) {
                element.style.fontFamily = FONT_FAMILIES[font];
            } else if (name === 'ql-direction-rtl') {
                element.setAttribute('dir', 'rtl');
            }
            element.classList.remove(name);
        });
        if (!element.classList.length) {
            element.removeAttribute('class');
        }
    });
};

export const normalizeLegacyRichText = (html: string | null): string => {
    if (!html || (!html.includes('ql-') && !html.includes('data-list'))) {
        return html ?? '';
    }
    const root = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body;
    root.querySelectorAll('.ql-ui').forEach(element => element.remove());
    root.querySelectorAll('pre.ql-syntax').forEach(element => element.classList.remove('ql-syntax'));
    convertCodeBlocks(root);
    convertLists(root);
    convertClasses(root);
    return sanitizeHtml(root.innerHTML);
};
